/**
 * Unit-quaternion helpers for orientation. Plain data (`{ x, y, z, w }`), pure
 * functions. `w` is the scalar part. Rotations compose left-to-right as
 * `mul(a, b)` = "apply b, then a" (Hamilton product).
 *
 * Euler convention: intrinsic Z-Y-X (yaw, then pitch, then roll), i.e.
 * `q = qZ(yaw) · qY(pitch) · qX(roll)`. With the ENU world + FLU body frame
 * (see ./index.ts) this reads as heading, then nose-up, then right-roll.
 */
import type { Vec3 } from './vec3';

export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

export const IDENTITY_QUAT: Readonly<Quat> = Object.freeze({ x: 0, y: 0, z: 0, w: 1 });

export const quat = (x = 0, y = 0, z = 0, w = 1): Quat => ({ x, y, z, w });
export const cloneQuat = (q: Quat): Quat => ({ x: q.x, y: q.y, z: q.z, w: q.w });

export const quatLength = (q: Quat): number =>
  Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w);

export const normalizeQuat = (q: Quat): Quat => {
  const len = quatLength(q);
  if (len < 1e-12) return { ...IDENTITY_QUAT };
  const inv = 1 / len;
  return { x: q.x * inv, y: q.y * inv, z: q.z * inv, w: q.w * inv };
};

export const conjugate = (q: Quat): Quat => ({ x: -q.x, y: -q.y, z: -q.z, w: q.w });

/** Hamilton product. `mul(a, b)` rotates by `b` first, then by `a`. */
export const mul = (a: Quat, b: Quat): Quat => ({
  w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
  y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
  z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
});

export const quatFromAxisAngle = (axis: Vec3, angleRad: number): Quat => {
  const len = Math.sqrt(axis.x * axis.x + axis.y * axis.y + axis.z * axis.z);
  if (len < 1e-12) return { ...IDENTITY_QUAT };
  const half = angleRad * 0.5;
  const s = Math.sin(half) / len;
  return { x: axis.x * s, y: axis.y * s, z: axis.z * s, w: Math.cos(half) };
};

/** Rotate a vector by a unit quaternion: `q · v · q*`. */
export const rotateVec = (q: Quat, v: Vec3): Vec3 => {
  // t = 2 * cross(q.xyz, v); v' = v + q.w * t + cross(q.xyz, t)
  const tx = 2 * (q.y * v.z - q.z * v.y);
  const ty = 2 * (q.z * v.x - q.x * v.z);
  const tz = 2 * (q.x * v.y - q.y * v.x);
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  };
};

export interface Euler {
  /** rotation about body +X (FLU: right-roll positive). */
  roll: number;
  /** rotation about body +Y (FLU: nose-up positive). */
  pitch: number;
  /** rotation about world/body +Z (ENU: counter-clockwise-from-above positive). */
  yaw: number;
}

/** Build a quaternion from intrinsic Z-Y-X Euler angles (radians). */
export const quatFromEuler = ({ roll, pitch, yaw }: Euler): Quat => {
  const cr = Math.cos(roll * 0.5);
  const sr = Math.sin(roll * 0.5);
  const cp = Math.cos(pitch * 0.5);
  const sp = Math.sin(pitch * 0.5);
  const cy = Math.cos(yaw * 0.5);
  const sy = Math.sin(yaw * 0.5);
  return {
    w: cr * cp * cy + sr * sp * sy,
    x: sr * cp * cy - cr * sp * sy,
    y: cr * sp * cy + sr * cp * sy,
    z: cr * cp * sy - sr * sp * cy,
  };
};

/** Recover intrinsic Z-Y-X Euler angles (radians). Pitch is clamped to ±π/2. */
export const eulerFromQuat = (q: Quat): Euler => {
  const { x, y, z, w } = q;
  // roll (X)
  const sinrCosp = 2 * (w * x + y * z);
  const cosrCosp = 1 - 2 * (x * x + y * y);
  const roll = Math.atan2(sinrCosp, cosrCosp);
  // pitch (Y)
  const sinp = 2 * (w * y - z * x);
  const pitch = Math.abs(sinp) >= 1 ? Math.sign(sinp) * (Math.PI / 2) : Math.asin(sinp);
  // yaw (Z)
  const sinyCosp = 2 * (w * z + x * y);
  const cosyCosp = 1 - 2 * (y * y + z * z);
  const yaw = Math.atan2(sinyCosp, cosyCosp);
  return { roll, pitch, yaw };
};

export const dotQuat = (a: Quat, b: Quat): number => a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;

/** Shortest-arc spherical interpolation. */
export const slerp = (a: Quat, b: Quat, t: number): Quat => {
  let d = dotQuat(a, b);
  let bx = b.x;
  let by = b.y;
  let bz = b.z;
  let bw = b.w;
  if (d < 0) {
    d = -d;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  if (d > 0.9995) {
    // near-parallel: linear + renormalize
    return normalizeQuat({
      x: a.x + (bx - a.x) * t,
      y: a.y + (by - a.y) * t,
      z: a.z + (bz - a.z) * t,
      w: a.w + (bw - a.w) * t,
    });
  }
  const theta0 = Math.acos(d);
  const theta = theta0 * t;
  const sinTheta0 = Math.sin(theta0);
  const s0 = Math.cos(theta) - (d * Math.sin(theta)) / sinTheta0;
  const s1 = Math.sin(theta) / sinTheta0;
  return {
    x: a.x * s0 + bx * s1,
    y: a.y * s0 + by * s1,
    z: a.z * s0 + bz * s1,
    w: a.w * s0 + bw * s1,
  };
};

/**
 * Integrate a body-frame angular rate (rad/s) over `dt` using the exponential
 * map, so a constant rate integrated over a full turn returns exactly to the
 * start orientation. Returns a normalized quaternion.
 */
export const integrateAngularRate = (q: Quat, bodyRate: Vec3, dt: number): Quat => {
  const wx = bodyRate.x;
  const wy = bodyRate.y;
  const wz = bodyRate.z;
  const speed = Math.sqrt(wx * wx + wy * wy + wz * wz);
  const angle = speed * dt;
  let dq: Quat;
  if (angle < 1e-9) {
    dq = { x: 0, y: 0, z: 0, w: 1 };
  } else {
    const half = angle * 0.5;
    const s = Math.sin(half) / speed;
    dq = { x: wx * s, y: wy * s, z: wz * s, w: Math.cos(half) };
  }
  // body-frame increment multiplies on the right
  return normalizeQuat(mul(q, dq));
};

export const approxEqualQuat = (a: Quat, b: Quat, eps = 1e-6): boolean => {
  // quaternions q and -q represent the same rotation
  const same =
    Math.abs(a.x - b.x) <= eps &&
    Math.abs(a.y - b.y) <= eps &&
    Math.abs(a.z - b.z) <= eps &&
    Math.abs(a.w - b.w) <= eps;
  const neg =
    Math.abs(a.x + b.x) <= eps &&
    Math.abs(a.y + b.y) <= eps &&
    Math.abs(a.z + b.z) <= eps &&
    Math.abs(a.w + b.w) <= eps;
  return same || neg;
};
